import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Check, Loader2, Crown, Zap, Building2, ArrowRight,
  Users, Calendar, Headphones, ShoppingBag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 99,
    description: 'Perfect for small businesses getting started',
    features: [
      '10 shifts/month',
      'Basic support',
      'Job board access',
      'Staff scheduling'
    ],
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    popular: false
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 299,
    description: 'For growing businesses with higher demand',
    features: [
      '50 shifts/month',
      'Priority support',
      'Wholesale vendor access',
      'Advanced reporting',
      'Bulk shift creation'
    ],
    icon: Crown,
    color: 'from-indigo-500 to-purple-500',
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 599,
    description: 'Unlimited power for large organizations',
    features: [
      'Unlimited shifts',
      'Dedicated account manager',
      'Full marketplace access',
      'Custom integrations',
      'White-label options',
      'Priority vendor matching'
    ],
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
    popular: false
  }
];

export default function B2BPricingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  useEffect(() => {
    if (user && token) {
      fetchCurrentSubscription();
    } else {
      setLoadingSubscription(false);
    }
  }, [user, token]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSubscription(response.data);
    } catch (err) {
      // No subscription
      setCurrentSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    setLoadingPlan(planId);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        {
          plan: planId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950 pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-indigo-500/20 text-indigo-300 border-indigo-500/30">
            B2B Membership
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Power Your Event Business
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Get access to reliable staff, wholesale vendors, and powerful tools to scale your events operation.
          </p>
        </div>

        {/* Current Subscription Banner */}
        {currentSubscription && (
          <div className="mb-8 bg-green-500/10 border border-green-500/30 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center gap-2 text-green-400 mb-2">
              <Check className="h-5 w-5" />
              <span className="font-semibold">Active Subscription</span>
            </div>
            <p className="text-foreground">
              You're on the <span className="font-bold capitalize">{currentSubscription.plan}</span> plan.
              Valid until {new Date(currentSubscription.end_date).toLocaleDateString()}
            </p>
            <Button 
              variant="outline" 
              className="mt-4 border-green-500/30 text-green-400 hover:bg-green-500/10"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentSubscription?.plan === plan.id;
            
            return (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden bg-muted/50 border-border backdrop-blur-sm ${
                  plan.popular ? 'ring-2 ring-indigo-500' : ''
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-indigo-500 text-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0">
                    <div className="bg-green-500 text-foreground text-xs font-bold px-3 py-1 rounded-br-lg">
                      CURRENT PLAN
                    </div>
                  </div>
                )}
                
                <CardHeader className="pb-0">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                    <Icon className="h-6 w-6 text-foreground" />
                  </div>
                  <CardTitle className="text-2xl text-foreground">{plan.name}</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="py-6">
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground ml-2">/month</span>
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700' 
                        : 'bg-muted hover:bg-accent'
                    }`}
                    size="lg"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loadingPlan === plan.id || isCurrentPlan || loadingSubscription}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {loadingPlan === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      <>
                        <Check className="mr-2 h-4 w-4" />
                        Current Plan
                      </>
                    ) : (
                      <>
                        Get Started
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Features Grid */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Everything You Need to Scale
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-muted/30 rounded-xl p-6 border border-border/50">
              <Users className="h-8 w-8 text-indigo-400 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Vetted Staff</h3>
              <p className="text-muted-foreground text-sm">
                Access a pool of pre-screened bartenders, servers, and event professionals.
              </p>
            </div>
            <div className="bg-muted/30 rounded-xl p-6 border border-border/50">
              <Calendar className="h-8 w-8 text-purple-400 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Easy Scheduling</h3>
              <p className="text-muted-foreground text-sm">
                Post shifts and fill positions in minutes, not days.
              </p>
            </div>
            <div className="bg-muted/30 rounded-xl p-6 border border-border/50">
              <ShoppingBag className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Wholesale Access</h3>
              <p className="text-muted-foreground text-sm">
                Get preferred pricing from verified vendors in the marketplace.
              </p>
            </div>
            <div className="bg-muted/30 rounded-xl p-6 border border-border/50">
              <Headphones className="h-8 w-8 text-green-400 mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Priority Support</h3>
              <p className="text-muted-foreground text-sm">
                Get help when you need it with dedicated support channels.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ or CTA */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border-indigo-500/30 max-w-2xl mx-auto">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Not sure which plan is right for you?
              </h3>
              <p className="text-muted-foreground mb-6">
                Our team is here to help you find the perfect fit for your business.
              </p>
              <Button 
                variant="outline" 
                className="border-white/30 text-foreground hover:bg-white/10"
                onClick={() => window.location.href = 'mailto:hello@eventism.com'}
              >
                Contact Sales
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
